import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { RequireAuth } from '@/components/RequireAuth';
import { IN_SAFE_APP } from '@/lib/safeApp';
import { ClaimsPage } from '@/pages/ClaimsPage';
import { ConstructionDailyPage } from '@/pages/ConstructionDailyPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { ContractPage } from '@/pages/ContractPage';
import { DailyLogPage } from '@/pages/DailyLogPage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { DrawingsPage } from '@/pages/DrawingsPage';
import { EquipmentLogsPage } from '@/pages/EquipmentLogsPage';
import { HomePage } from '@/pages/HomePage';
import { SafeAppHome } from '@/pages/SafeAppHome';
import { InspectionDetailPage } from '@/pages/InspectionDetailPage';
import { InspectionsPage } from '@/pages/InspectionsPage';
import { InvitePage } from '@/pages/InvitePage';
import { LaborPage } from '@/pages/LaborPage';
import { LoginPage } from '@/pages/LoginPage';
import { MaterialSlipsPage } from '@/pages/MaterialSlipsPage';
import { MembersPage } from '@/pages/MembersPage';
import { MyPage } from '@/pages/MyPage';
import { DefectPage } from '@/pages/DefectPage';
import { NcrPage } from '@/pages/NcrPage';
import { OverviewPage } from '@/pages/OverviewPage';
import { ProgressPage } from '@/pages/ProgressPage';
import { PurchaseOrdersPage } from '@/pages/PurchaseOrdersPage';
import { RfqDetailPage } from '@/pages/RfqDetailPage';
import { RfqsPage } from '@/pages/RfqsPage';
import { SafetyInspectionsPage } from '@/pages/SafetyInspectionsPage';
import { SafetyPage } from '@/pages/SafetyPage';
import { SceneryPage } from '@/pages/SceneryPage';
import { SupervisionLogPage } from '@/pages/SupervisionLogPage';
import { TestReportsPage } from '@/pages/TestReportsPage';
import { WorkersPage } from '@/pages/WorkersPage';

/*
  DOC-82 §6-4 의 1차 화면. 경로에 현장이 들어가는 것들은 전부 `/sites/:siteId/` 아래에 둔다 —
  <b>현장을 고르지 않고는 아무것도 열리지 않아야</b> 데이터 경계가 화면에서도 같은 모양이 된다.

  홈(`/`)이 곧 고른 현장의 대시보드라, 「현장 메뉴를 나열하는 중간 화면」은 두지 않는다 —
  위젯이 이미 그 현장의 요약이고 각 위젯의 「전체보기」가 곧 그 메뉴다.

  화면 분기(협력사·감리)는 각 화면이 `useSite().partyType` 으로 한다. 라우트를 둘로 나누지
  않은 것은, 나누면 「감리에게만 있는 경로」가 생기고 그 목록이 곧 권한 표가 되기 때문이다 —
  진짜 판정은 서버가 한다.
*/
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* 초대 수락 — 문지기 밖이다. 아직 계정이 없는 사람이 두드리는 문이라서다(DOC-82 §3-1). */}
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/*
          홈은 `/` 다. `/home` 도 같은 화면으로 받는다 — 주소를 손으로 치거나 링크를 만들 때
          자연스럽게 떠오르는 이름이라, 없으면 아무 데도 없는 곳으로 튕긴다.
        */}
        {/* 「보미건설 안전관리」 앱 안에서는 홈이 곧 첫 안전 화면이다(DOC-140 · `lib/safeApp.ts`). */}
        <Route path="/" element={IN_SAFE_APP ? <SafeAppHome /> : <HomePage />} />
        <Route path="/home" element={IN_SAFE_APP ? <SafeAppHome /> : <HomePage />} />
        <Route path="/members" element={<MembersPage />} />
        {/*
          마이페이지 — 현장과 무관한 전역 자리다. 들어오는 문은 셋이고 전부 같은 곳을 가리킨다:
          관문(`/login`)의 프로필 카드 · 머리줄의 아바타 · 「로그아웃」 옆 줄(사용자 지시 2026-09-30).
        */}
        <Route path="/mypage" element={<MyPage />} />
        {/*
          견적·입찰 — 현장이 경로에 없는 둘째 자리다(DOC-98 §5-1). 공고가 현장을 들고 있어
          「어느 현장의 목록인가」가 아니라 「우리가 초청받은 것 전부」가 한 화면이다.
        */}
        <Route path="/rfqs" element={<RfqsPage />} />
        <Route path="/rfqs/:rfqId" element={<RfqDetailPage />} />
        {/* 현장의 현재 상태 — 협력사·감리가 같은 것을 본다(경계가 site_id 하나다). */}
        <Route path="/sites/:siteId/overview" element={<OverviewPage />} />
        <Route path="/sites/:siteId/progress" element={<ProgressPage />} />
        <Route path="/sites/:siteId/contacts" element={<ContactsPage />} />
        <Route path="/sites/:siteId/contract" element={<ContractPage />} />
        <Route path="/sites/:siteId/claims" element={<ClaimsPage />} />
        <Route path="/sites/:siteId/labor" element={<LaborPage />} />
        <Route path="/sites/:siteId/daily" element={<DailyLogPage />} />
        <Route path="/sites/:siteId/documents" element={<DocumentsPage />} />
        <Route path="/sites/:siteId/inspections" element={<InspectionsPage />} />
        <Route path="/sites/:siteId/inspections/:inspectionId" element={<InspectionDetailPage />} />
        <Route path="/sites/:siteId/safety" element={<SafetyPage />} />
        {/* 옛 주소 — 출역 화면의 「투입 인력」 탭으로 보낸다(DOC-84 §7 ②). */}
        <Route path="/sites/:siteId/workers" element={<WorkersPage />} />
        <Route path="/sites/:siteId/drawings" element={<DrawingsPage />} />
        <Route path="/sites/:siteId/supervision" element={<SupervisionLogPage />} />
        <Route path="/sites/:siteId/safety-inspections" element={<SafetyInspectionsPage />} />
        <Route path="/sites/:siteId/ncrs" element={<NcrPage />} />
        <Route path="/sites/:siteId/defects" element={<DefectPage />} />
        <Route path="/sites/:siteId/test-reports" element={<TestReportsPage />} />
        <Route path="/sites/:siteId/purchase" element={<PurchaseOrdersPage />} />
        <Route path="/sites/:siteId/equipment" element={<EquipmentLogsPage />} />
        {/* 직원의 현장 입력 — 웹 메뉴 권한으로 열린다(`lib/field.ts`). */}
        <Route path="/sites/:siteId/construction-daily" element={<ConstructionDailyPage />} />
        <Route path="/sites/:siteId/material-slips" element={<MaterialSlipsPage />} />
        <Route path="/sites/:siteId/scenery" element={<SceneryPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
